import React from "react";
import { isNullOrWhiteSpace } from "../../shared/string";
import BackgroundBlockPattern, {
  BackgroundBlockPatternType
} from "../BackgroundBlockPattern";
import { PortableTextColorThemeProps } from "../PortableText/PortableText";
import HelpCard from "./HelpCard";
import { SanityHelpCardContact } from "./HelpCardContact";
import { SanityHelpCardCta } from "./HelpCardCta";
import { SanityHelpCardIRContact } from "./HelpCardIRContact";
import { SanityHelpCardPressReleaseSignup } from "./HelpCardPressReleaseSignup";
import { SanityHelpCardText } from "./HelpCardText";
import { SanityHelpCardWithRating } from "./HelpCardWithRating";

export type SanityHelpCard =
  | SanityHelpCardContact
  | SanityHelpCardCta
  | SanityHelpCardIRContact
  | SanityHelpCardPressReleaseSignup
  | SanityHelpCardText
  | SanityHelpCardWithRating;

export interface HelpCardPairProps {
  backgroundPattern?: BackgroundBlockPatternType;
  title?: string;
  subtitle?: string;
  portableTextColorThemeProps?: PortableTextColorThemeProps;
  splitCards?: boolean;
  cardLeft?: SanityHelpCard[];
  cardRight?: SanityHelpCard[];
}

const HelpCardPair = (props: HelpCardPairProps) => {
  const {
    title,
    subtitle,
    portableTextColorThemeProps,
    backgroundPattern,
    cardLeft,
    cardRight,
    splitCards
  } = props;

  const {
    elementClasses: { h3Subheader: subtitleClasses = "text-subheader-primary" } = {}
  } = portableTextColorThemeProps || {};

  let leftCardAdditionalClassName = "";

  // Add additional padding to the left card if the right card overlaps it
  if (cardRight?.length && splitCards !== true) {
    leftCardAdditionalClassName = "md:pr-16 lg:pr-8";
  }

  // add classes to split card pairs for custom stacking style
  let cardPairClassName = "";
  if (splitCards) {
    cardPairClassName = "split-card-pair";
    if (isNullOrWhiteSpace(title)) {
      cardPairClassName += " split-card-pair--no-title";
    }
  }

  return (
    <aside
      className={`${cardPairClassName} relative max-w-none px-0 anticimex-help-card-pair`}
    >
      {backgroundPattern && <BackgroundBlockPattern {...backgroundPattern} />}
      <div
        className={`relative ${
          splitCards ? "max-w-10xl" : "max-w-screen-xl"
        } mx-auto z-10 md:px-6`}
      >
        {!isNullOrWhiteSpace(title) ? (
          <div className="mb-8 px-4 md:mb-24 md:px-0 lg:mb-32">
            <h2 className="text-current mb-2">{title}</h2>
            {!isNullOrWhiteSpace(subtitle) && (
              <h3
                className={`${subtitleClasses} text-base lg:text-xl uppercase font-bold`}
              >
                {subtitle}
              </h3>
            )}
          </div>
        ) : (
          <div className="md:pt-16"></div>
        )}
        <div className="flex flex-col md:flex-row">
          {!!cardLeft?.length && (
            <HelpCard
              {...cardLeft[0]}
              className={`${
                cardRight?.length
                  ? "min-h-help-card-small"
                  : "min-h-help-card-small lg:min-h-help-card"
              }`}
              innerAdditionalClassName={leftCardAdditionalClassName}
              wideImage={!cardRight?.length}
            />
          )}
          {!!cardRight?.length && (
            <HelpCard
              rightAligned
              splitCards={splitCards}
              {...cardRight[0]}
              className="min-h-help-card-small"
            />
          )}
        </div>
      </div>
    </aside>
  );
};

export default HelpCardPair;
